import { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { ExchangeChart, ExchangeRates } from '@/features/exchange';
import { ICurrencyAccount } from '@/entities/currency-account';
import { useExchangeRates } from '@/entities/rates/model';

import { ChevronIcon, CloseIcon } from '@/shared/assets';
import { requestsClient, VabsArRateChartPeriod } from '@/shared/api';
import { closeModal, cn } from '@/shared/lib';
import { Button, ModalTemplate, Tabs, TabsList, TabsTrigger, Typography } from '@/shared/ui';
import { useVabsAccountsStore } from '@/entities/currency-account';

interface ExchangeRatePair {
  id: string;
  isReversed: boolean;
}

const TIMEFRAMES = [
  { value: '1d', label: '1D', period: VabsArRateChartPeriod.OneDay },
  { value: '1w', label: '1W', period: VabsArRateChartPeriod.OneWeek },
  { value: '1m', label: '1M', period: VabsArRateChartPeriod.OneMonth },
  { value: '6m', label: '6M', period: VabsArRateChartPeriod.SixMonths },
  { value: '1y', label: '1Y', period: VabsArRateChartPeriod.OneYear },
  { value: 'all', label: 'All', period: VabsArRateChartPeriod.All },
];

interface RateModalProps {
  from?: string;
  to?: string;
  modalId?: string;
}

const findCurrencyByCode = (
  accounts: ICurrencyAccount[],
  code?: string,
): ICurrencyAccount | undefined => {
  const normalizedCode = code?.trim().toUpperCase();
  if (!normalizedCode) return undefined;
  return accounts.find((account) => account.code === normalizedCode);
};

export const RateModal = ({
  from = 'EUR',
  to = 'USD',
  modalId = 'exchange-rate-modal',
}: RateModalProps) => {
  const accounts = useVabsAccountsStore((state) => state.accounts);
  const [timeframe, setTimeframe] = useState(TIMEFRAMES[0].value);

  const activeAccounts = useMemo(
    () => accounts.filter((account) => account.status === 'ACTIVE'),
    [accounts],
  );

  const fromCurrency = useMemo(
    () => findCurrencyByCode(activeAccounts, from) || activeAccounts[0],
    [activeAccounts, from],
  );
  const toCurrency = useMemo(() => {
    const matched = findCurrencyByCode(activeAccounts, to);
    if (matched && matched.code !== fromCurrency?.code) return matched;
    return activeAccounts.find((item) => item.code !== fromCurrency?.code) || fromCurrency;
  }, [activeAccounts, fromCurrency?.code, to]);

  const chartPeriod = useMemo(
    () => TIMEFRAMES.find((item) => item.value === timeframe)?.period,
    [timeframe],
  );
  const fromCurrencyCode = fromCurrency?.code?.trim().toUpperCase();
  const toCurrencyCode = toCurrency?.code?.trim().toUpperCase();

  const { data: selectedPair } = useQuery<ExchangeRatePair | null>({
    queryKey: ['exchange-rate-pair', fromCurrencyCode, toCurrencyCode],
    enabled:
      Boolean(fromCurrencyCode) &&
      Boolean(toCurrencyCode) &&
      fromCurrencyCode !== toCurrencyCode,
    queryFn: async () => {
      if (!fromCurrencyCode || !toCurrencyCode) return null;

      const directPairTicker = `${fromCurrencyCode}/${toCurrencyCode}`;
      const reversePairTicker = `${toCurrencyCode}/${fromCurrencyCode}`;

      const { vabsArAssetPairs } = await requestsClient.vabsArAssetPairs({
        input: {
          pairTickers: directPairTicker,
          pagination: {
            limit: 20,
            skip: 0,
          },
        },
      });

      const pair =
        vabsArAssetPairs.nodes.find((item) => item.pairTicker === directPairTicker) ??
        vabsArAssetPairs.nodes.find((item) => item.pairTicker === reversePairTicker);

      if (!pair) return null;

      return {
        id: pair.id,
        isReversed: pair.pairTicker === reversePairTicker,
      };
    },
    staleTime: Number.POSITIVE_INFINITY,
  });

  const { data: selectedRates = [] } = useExchangeRates({
    fromCurrency,
    toCurrency,
    chartPeriod,
    assetPair: selectedPair || undefined,
  });

  const currentRate = selectedRates[selectedRates.length - 1]?.rate;
  const startRate = selectedRates[0]?.rate ?? currentRate;
  const change = currentRate - startRate;
  const percentChange = startRate ? (change / startRate) * 100 : 0;
  const hasRates = selectedRates.length > 0;
  const isPositive = hasRates && change >= 0;
  const currentRateLabel = hasRates && Number.isFinite(currentRate) ? currentRate.toFixed(4) : '--';
  const rateChangeLabel = hasRates && Number.isFinite(change)
    ? `${isPositive ? '+' : '-'}${Math.abs(change).toFixed(3)}`
    : '--';
  const percentChangeLabel = hasRates && Number.isFinite(percentChange)
    ? `${Math.abs(percentChange).toFixed(2)}%`
    : '--';
  const fromLabel = fromCurrency?.code || from.toUpperCase();
  const toLabel = toCurrency?.code || to.toUpperCase();
  const toSymbol = toCurrency?.symbol || toLabel;

  const exchangeRates: ExchangeRates = {
    from: fromLabel,
    to: toLabel,
    rates: selectedRates,
  };

  return (
    <ModalTemplate
      id={modalId}
      className="relative gap-4 pt-5 px-1 pb-6"
      isHasCloseBtn={false}
    >
      <Button
        type="button"
        variant="base-300"
        size="rounded-md"
        className="absolute right-4 top-4"
        onClick={() => { closeModal(modalId); }}
        aria-label="Close rate modal"
      >
        <CloseIcon className="size-4" />
      </Button>
      <div className="flex flex-col gap-1 px-4">
        <Typography variant="14-r" color="text-base-100">
          {fromLabel} to {toLabel}
        </Typography>
        <div className="flex flex-col items-start gap-1">
          <p className="heading-s">
            {currentRateLabel} {toSymbol}
          </p>
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'typography-14r font-medium text-base-100',
              )}
            >
              {rateChangeLabel}
            </span>
            <span
              className={cn(
                'typography-14r font-medium flex items-center',
                hasRates ? (isPositive ? 'text-primary' : 'text-negative-500') : 'text-base-100',
              )}
            >
              <ChevronIcon className={cn('w-4 h-4', isPositive ? '-rotate-90' : 'rotate-90')} />
              {percentChangeLabel}
            </span>
            <Typography variant="14-r" className="text-base-100">
              Today
            </Typography>
          </div>
        </div>
      </div>
      <ExchangeChart exchangeRates={exchangeRates} />
      <Tabs value={timeframe} onValueChange={setTimeframe}>
        <TabsList className="mt-2 w-full justify-between gap-0 bg-base-300 px-1 py-1">
          {TIMEFRAMES.map((item) => (
            <TabsTrigger
              key={item.value}
              value={item.value}
              className="!shrink-0 pl-5 pr-3.5 text-[14px] font-medium"
            >
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <Typography variant="13-r" className="text-base-100 text-center px-2">
        This is the midpoint between buy and sell rates. Actual rate varies
        based on whether you buy or sell. Pricing data is provided by FCB.
      </Typography>
    </ModalTemplate>
  );
};
